import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { createSubject } from "../services/subject";
import { listFrequencies } from "../services/teacher";
import WizardSteps from "./shared/WizardSteps";
import { levelKey } from "../utils/classesLabels";

const TOTAL_STEPS = 3;

const initialState = {
  subjectName: "",
  levelId: "",
  code: "",
  abbreviation: "",
  totalCredits: "",
  isCurricular: true,
  hasDispensa: false,
};

// Split out of the former DisciplinasLayer (which mixed the wizard with the
// subject table). Mirrors CadastrarProdutoLayer: this screen is hidden from
// the menu and only reachable through the "Adicionar Disciplina" button on
// ListaDisciplinasLayer — see App.js route `/academico/disciplinas/cadastrar`.
const CadastrarDisciplinaLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState(initialState);
  const [frequencies, setFrequencies] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const stepLabels = [t("disciplina.wizard.step.data"), t("disciplina.wizard.step.settings"), t("disciplina.wizard.step.confirm")];

  useEffect(() => {
    listFrequencies().then((resp) => setFrequencies(resp.data || [])).catch(() => setFrequencies([]));
  }, []);

  const frequencyName = (id) => frequencies.find((f) => f.id === id)?.description || "-";

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setChecked = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));

  const nextStep = () => {
    if (currentStep === 1 && (!form.subjectName || !form.levelId)) {
      toast.error(t(levelKey("disciplina.error.requiredFields")));
      return;
    }
    if (currentStep < TOTAL_STEPS) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const restart = () => {
    setForm(initialState);
    setCurrentStep(1);
    setDone(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await createSubject({
        subjectName: form.subjectName,
        levelId: form.levelId,
        code: form.code,
        abbreviation: form.abbreviation,
        totalCredits: form.totalCredits ? Number(form.totalCredits) : 0,
        isCurricular: form.isCurricular,
        hasDispensa: form.hasDispensa,
      });
      setDone(true);
    } catch (err) {
      toast.error(t("disciplina.error.create"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='mb-4 text-xl'>{t("disciplina.wizard.title")}</h6>
            <p className='text-neutral-500'>{t("disciplina.wizard.subtitle")}</p>

            <div className='form-wizard'>
              <WizardSteps steps={stepLabels} currentStep={done ? TOTAL_STEPS + 1 : currentStep} />

              {!done && currentStep === 1 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("disciplina.field.subjectName")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="mdi:book-open-variant-outline" />
                        </span>
                        <input type='text' className='form-control' value={form.subjectName} onChange={setField("subjectName")} required />
                      </div>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t(levelKey("disciplina.field.level"))}</label>
                      <select className='form-select' value={form.levelId} onChange={setField("levelId")} required>
                        <option value=''>{t(levelKey("turma.field.educationLevel.placeholder"))}</option>
                        {frequencies.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("disciplina.field.code")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:hashtag-square-outline" />
                        </span>
                        <input type='text' className='form-control' value={form.code} onChange={setField("code")} />
                      </div>
                    </div>
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("disciplina.field.abbreviation")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="mdi:book-open-variant-outline" />
                        </span>
                        <input type='text' className='form-control' value={form.abbreviation} onChange={setField("abbreviation")} />
                      </div>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end mt-24'>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 2 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='row gy-3'>
                    <div className='col-sm-4'>
                      <label className='form-label'>{t("disciplina.field.totalCredits")}</label>
                      <div className="icon-field">
                        <span className="icon">
                          <Icon icon="solar:dollar-minimalistic-outline" />
                        </span>
                        <input type='number' min='0' className='form-control' value={form.totalCredits} onChange={setField("totalCredits")} />
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div className='form-check mt-32'>
                        <input className='form-check-input' type='checkbox' id='isCurricular' checked={form.isCurricular} onChange={setChecked("isCurricular")} />
                        <label className='form-check-label' htmlFor='isCurricular'>
                          {t("disciplina.field.isCurricular")}
                        </label>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div className='form-check mt-32'>
                        <input className='form-check-input' type='checkbox' id='hasDispensa' checked={form.hasDispensa} onChange={setChecked("hasDispensa")} />
                        <label className='form-check-label' htmlFor='hasDispensa'>
                          {t("disciplina.field.hasDispensa")}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button type='button' className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32' onClick={prevStep}>
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-next-btn btn btn-primary-600 px-32' onClick={nextStep}>
                      {t("global.next")} <i className='ri-arrow-right-line' />
                    </button>
                  </div>
                </fieldset>
              )}

              {!done && currentStep === 3 && (
                <fieldset className='wizard-fieldset show'>
                  <div className='alert alert-info'>{t("disciplina.confirm.description")}</div>
                  <ul className='list-unstyled d-flex flex-column gap-8'>
                    <li>
                      <strong>{t("disciplina.field.subjectName")}:</strong> {form.subjectName}
                    </li>
                    <li>
                      <strong>{t(levelKey("disciplina.field.level"))}:</strong> {frequencyName(form.levelId)}
                    </li>
                    <li>
                      <strong>{t("disciplina.field.code")}:</strong> {form.code || "-"}
                    </li>
                    <li>
                      <strong>{t("disciplina.field.totalCredits")}:</strong> {form.totalCredits || "-"}
                    </li>
                    <li>
                      <strong>{t("disciplina.field.isCurricular")}:</strong> {form.isCurricular ? t("global.yes") : t("global.no")}
                    </li>
                  </ul>
                  <div className='form-group d-flex align-items-center justify-content-end gap-8 mt-24'>
                    <button
                      type='button'
                      className='form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32'
                      onClick={prevStep}
                      disabled={submitting}
                    >
                      {t("global.previous")}
                    </button>
                    <button type='button' className='form-wizard-submit btn btn-primary-600 px-32' onClick={handleSubmit} disabled={submitting}>
                      {submitting ? (
                        t("global.loading")
                      ) : (
                        <>
                          <i className='ri-check-line' /> {t("global.confirm")}
                        </>
                      )}
                    </button>
                  </div>
                </fieldset>
              )}

              {done && (
                <fieldset className='wizard-fieldset show'>
                  <div className='text-center mb-40'>
                    <span className='w-64-px h-64-px bg-success-focus text-success-main rounded-circle d-inline-flex justify-content-center align-items-center mb-16 text-2xl'>
                      <i className='ri-check-line' />
                    </span>
                    <h6 className='text-md text-neutral-600'>{t("disciplina.success.created")}</h6>
                  </div>
                  <div className='form-group d-flex align-items-center justify-content-center gap-8'>
                    <button type='button' className='btn btn-neutral-500 border-neutral-100 px-32' onClick={restart}>
                      {t("disciplina.action.createAnother")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 px-32'
                      onClick={() => navigate("/academico/disciplinas")}
                    >
                      <i className='ri-arrow-left-line' /> {t("disciplina.breadcrumb.list")}
                    </button>
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarDisciplinaLayer;
